<template>
  <div class="min-h-screen bg-gray-50 py-8">
    <div class="container-custom">
      <!-- Back Button -->
      <button
        @click="$router.back()"
        class="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
      >
        <ArrowLeftIcon class="h-5 w-5 mr-2" />
        Voltar
      </button>
      
      <h1 class="text-2xl font-bold text-gray-900 mb-8">{{ $t('checkout.title') }}</h1>
      
      <!-- Stepper -->
      <div class="flex items-center justify-between mb-8">
        <div
          v-for="(step, index) in steps"
          :key="step.key"
          class="flex items-center"
        >
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center font-medium"
            :class="{
              'bg-orange-500 text-white': currentStep >= index,
              'bg-gray-200 text-gray-500': currentStep < index
            }"
          >
            {{ index + 1 }}
          </div>
          <span
            class="ml-2 hidden sm:block"
            :class="currentStep >= index ? 'text-gray-900 font-medium' : 'text-gray-500'"
          >
            {{ step.label }}
          </span>
          <div
            v-if="index < steps.length - 1"
            class="w-12 sm:w-24 h-1 mx-2 sm:mx-4"
            :class="currentStep > index ? 'bg-orange-500' : 'bg-gray-200'"
          />
        </div>
      </div>
      
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Form -->
        <div class="lg:col-span-2">
          <!-- Step 1: Customer Info -->
          <div v-if="currentStep === 0" class="card p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-6">{{ $t('checkout.customer.title') }}</h2>
            
            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.customer.name') }} *
                </label>
                <input
                  v-model="customer.name"
                  type="text"
                  class="w-full border rounded-lg px-4 py-2"
                  :placeholder="$t('checkout.customer.namePlaceholder')"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.customer.phone') }} *
                </label>
                <input
                  v-model="customer.phone"
                  type="tel"
                  class="w-full border rounded-lg px-4 py-2"
                  :placeholder="$t('checkout.customer.phonePlaceholder')"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.customer.email') }}
                </label>
                <input
                  v-model="customer.email"
                  type="email"
                  class="w-full border rounded-lg px-4 py-2"
                  :placeholder="$t('checkout.customer.emailPlaceholder')"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.customer.cpf') }}
                </label>
                <input
                  v-model="customer.cpf"
                  type="text"
                  inputmode="numeric"
                  maxlength="14"
                  class="w-full border rounded-lg px-4 py-2"
                  :placeholder="$t('checkout.customer.cpfPlaceholder')"
                >
              </div>
            </div>
          </div>
          
          <!-- Step 2: Delivery -->
          <div v-if="currentStep === 1" class="card p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-6">{{ $t('checkout.delivery.title') }}</h2>
            
            <!-- Delivery Type -->
            <div class="grid grid-cols-2 gap-4 mb-6">
              <button
                @click="delivery.type = 'delivery'"
                class="p-4 border-2 rounded-lg text-left transition-colors"
                :class="delivery.type === 'delivery' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'"
              >
                <TruckIcon class="h-6 w-6 mb-2" :class="delivery.type === 'delivery' ? 'text-orange-500' : 'text-gray-400'" />
                <p class="font-medium">{{ $t('checkout.delivery.type.delivery') }}</p>
              </button>
              
              <button
                @click="delivery.type = 'pickup'"
                class="p-4 border-2 rounded-lg text-left transition-colors"
                :class="delivery.type === 'pickup' ? 'border-orange-500 bg-orange-50' : 'border-gray-200'"
              >
                <BuildingStorefrontIcon class="h-6 w-6 mb-2" :class="delivery.type === 'pickup' ? 'text-orange-500' : 'text-gray-400'" />
                <p class="font-medium">{{ $t('checkout.delivery.type.pickup') }}</p>
              </button>
            </div>
            
            <!-- Delivery Address -->
            <div v-if="delivery.type === 'delivery'" class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.delivery.neighborhood') }} *
                </label>
                <select
                  v-if="!flatFeeEnabled"
                  v-model="delivery.neighborhood"
                  class="w-full border rounded-lg px-4 py-2"
                >
                  <option value="">{{ $t('checkout.delivery.selectNeighborhood') }}</option>
                  <option
                    v-for="neighborhood in neighborhoods"
                    :key="neighborhood.id"
                    :value="neighborhood.name"
                  >
                    {{ neighborhood.name }} - {{ formatCurrency(neighborhood.delivery_fee) }}
                  </option>
                </select>
                <template v-else>
                  <input
                    v-model="delivery.neighborhood"
                    type="text"
                    class="w-full border rounded-lg px-4 py-2"
                    placeholder="Digite seu bairro"
                  >
                  <p class="text-xs text-gray-500 mt-1">
                    Taxa de entrega única: {{ formatCurrency(companyStore.company?.delivery_flat_fee || 0) }}
                  </p>
                </template>
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.delivery.zipCode') }}
                </label>
                <input
                  v-model="delivery.zip_code"
                  type="text"
                  class="w-full border rounded-lg px-4 py-2"
                  :placeholder="$t('checkout.delivery.zipCodePlaceholder')"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.delivery.address') }} *
                </label>
                <input
                  v-model="delivery.address"
                  type="text"
                  class="w-full border rounded-lg px-4 py-2"
                  :placeholder="$t('checkout.delivery.addressPlaceholder')"
                >
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  {{ $t('checkout.delivery.complement') }}
                </label>
                <input
                  v-model="delivery.complement"
                  type="text"
                  class="w-full border rounded-lg px-4 py-2"
                  :placeholder="$t('checkout.delivery.complementPlaceholder')"
                >
              </div>
            </div>
          </div>
          
          <!-- Step 3: Payment -->
          <div v-if="currentStep === 2" class="card p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-6">{{ $t('checkout.payment.title') }}</h2>
            
            <div class="space-y-3">
              <button
                v-for="method in availablePaymentMethods"
                :key="method"
                @click="selectPaymentMethod(method)"
                class="w-full p-4 border-2 rounded-lg flex items-center gap-4 transition-colors"
                :class="payment.method === method ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'"
              >
                <component :is="getPaymentIcon(method)" class="h-6 w-6" />
                <span class="font-medium">{{ $t(`checkout.payment.methods.${method}`) }}</span>
              </button>
              
              <!-- Cash Change -->
              <div v-if="payment.method === 'cash'" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Troco para
                </label>
                <input
                  v-model.number="payment.change_for"
                  type="number"
                  class="w-full border rounded-lg px-4 py-2"
                  placeholder="Valor para troco"
                >
              </div>
              
              <!-- Mercado Pago Info -->
              <div v-if="payment.method === 'mercado_pago' && mercadoPagoEnabled" class="mt-4 p-4 bg-blue-50 rounded-lg">
                <p class="text-sm text-blue-700">
                  <InformationCircleIcon class="h-4 w-4 inline mr-1" />
                  Ao finalizar, você será redirecionado para o checkout seguro do Mercado Pago — lá você escolhe cartão e outras formas.
                </p>
              </div>
              
              <!-- PIX Info -->
              <div v-if="payment.method === 'pix' && mercadoPagoEnabled" class="mt-4 p-4 bg-blue-50 rounded-lg">
                <p class="text-sm text-blue-700">
                  <InformationCircleIcon class="h-4 w-4 inline mr-1" />
                  Ao finalizar, o QR Code do PIX será gerado aqui mesmo, sem sair da loja. O pedido é confirmado automaticamente após o pagamento.
                </p>
              </div>
            </div>
            
            <!-- No payment methods message -->
            <div v-if="availablePaymentMethods.length === 0" class="text-center py-8 text-gray-500">
              <p>Nenhuma forma de pagamento configurada.</p>
              <p class="text-sm">Formas padrão serão exibidas:</p>
              <div class="mt-4 space-y-3">
                <button
                  v-for="method in defaultPaymentMethods"
                  :key="method"
                  @click="selectPaymentMethod(method)"
                  class="w-full p-4 border-2 rounded-lg flex items-center gap-4 transition-colors"
                  :class="payment.method === method ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'"
                >
                  <component :is="getPaymentIcon(method)" class="h-6 w-6" />
                  <span class="font-medium">{{ $t(`checkout.payment.methods.${method}`) }}</span>
                </button>
              </div>
            </div>
          </div>
          
          <!-- Step 4: Review -->
          <div v-if="currentStep === 3" class="card p-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-6">{{ $t('checkout.review.title') }}</h2>
            
            <!-- Order Items -->
            <div class="mb-6">
              <h3 class="font-medium text-gray-700 mb-3">{{ $t('checkout.review.items') }}</h3>
              <div class="space-y-3">
                <div
                  v-for="item in cartStore.items"
                  :key="item.product.id"
                  class="flex justify-between items-center py-2 border-b"
                >
                  <div class="flex items-center gap-3">
                    <img
                      v-if="item.product.image_url"
                      :src="getImageUrl(item.product.image_url)"
                      class="w-12 h-12 rounded object-cover"
                    >
                    <div v-else class="w-12 h-12 rounded bg-gray-200 flex items-center justify-center">
                      <PhotoIcon class="h-6 w-6 text-gray-400" />
                    </div>
                    <div>
                      <p class="font-medium">{{ item.product.name }}</p>
                      <p class="text-sm text-gray-500">{{ item.quantity }}x {{ formatCurrency(item.product.current_price || item.product.price) }}</p>
                    </div>
                  </div>
                  <span>{{ formatCurrency(item.total) }}</span>
                </div>
              </div>
            </div>
            
            <!-- Delivery Info -->
            <div v-if="delivery.type === 'delivery'" class="mb-6">
              <h3 class="font-medium text-gray-700 mb-3">{{ $t('checkout.review.deliveryTo') }}</h3>
              <p class="text-sm text-gray-600">
                {{ delivery.address }}<br>
                {{ delivery.neighborhood }}<br>
                {{ delivery.complement }}<br>
                <span v-if="delivery.zip_code">{{ delivery.zip_code }}</span>
              </p>
            </div>
            
            <!-- Payment Method -->
            <div class="mb-6">
              <h3 class="font-medium text-gray-700 mb-3">{{ $t('checkout.review.paymentMethod') }}</h3>
              <p class="text-sm text-gray-600">
                {{ $t(`checkout.payment.methods.${payment.method}`) }}
              </p>
            </div>
          </div>
          
          <!-- Navigation Buttons -->
          <div class="flex justify-between mt-6">
            <button
              v-if="currentStep > 0"
              @click="currentStep--"
              class="px-6 py-3 rounded-lg border border-gray-300 font-medium hover:bg-gray-50"
            >
              {{ $t('app.back') }}
            </button>
            <button
              v-if="currentStep < steps.length - 1"
              @click="nextStep"
              class="ml-auto px-6 py-3 rounded-lg text-white font-medium bg-orange-600 hover:bg-orange-700"
            >
              {{ $t('app.next') }}
            </button>
            <button
              v-else
              @click="placeOrder"
              :disabled="placingOrder"
              class="ml-auto px-6 py-3 rounded-lg text-white font-medium flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-50"
            >
              <span v-if="placingOrder">{{ $t('checkout.processing') }}</span>
              <span v-else>{{ $t('checkout.review.placeOrder') }}</span>
            </button>
          </div>
        </div>
        
        <!-- Summary -->
        <div class="lg:col-span-1">
          <div class="card p-6 sticky top-24">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">
              {{ $t('checkout.review.orderSummary') }}
            </h2>
            
            <div class="space-y-3 mb-6">
              <div class="flex justify-between text-gray-600">
                <span>{{ $t('cart.subtotal') }}</span>
                <span>{{ cartStore.formattedSubtotal }}</span>
              </div>
              <div class="flex justify-between text-gray-600">
                <span>{{ $t('cart.deliveryFee') }}</span>
                <span>{{ deliveryFee > 0 ? formatCurrency(deliveryFee) : $t('cart.freeDelivery') }}</span>
              </div>
              <div class="border-t pt-3 flex justify-between text-lg font-semibold">
                <span>{{ $t('cart.total') }}</span>
                <span class="text-orange-600">{{ formatCurrency(total) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  
  <!-- PIX Payment Modal -->
  <div v-if="showPixModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-xl max-w-md w-full p-6">
      <div class="text-center mb-6">
        <h3 class="text-xl font-bold text-gray-900">Pagamento PIX</h3>
        <p class="text-gray-600">Escaneie o QR Code ou copie o código</p>
      </div>
      
      <div v-if="pixData" class="space-y-4">
        <!-- QR Code -->
        <div class="flex justify-center">
          <img 
            v-if="pixData.pix_qr_code" 
            :src="`data:image/png;base64,${pixData.pix_qr_code}`" 
            alt="QR Code PIX"
            class="w-48 h-48"
          >
        </div>
        
        <!-- PIX Code -->
        <div class="bg-gray-100 p-4 rounded-lg">
          <p class="text-xs text-gray-500 mb-1">Código PIX:</p>
          <div class="flex gap-2">
            <code class="flex-1 text-xs break-all">{{ pixData.pix_code }}</code>
            <button 
              @click="copyPixCode"
              class="text-orange-600 hover:text-orange-700 text-sm font-medium"
            >
              Copiar
            </button>
          </div>
        </div>
        
        <p class="text-sm text-gray-500 text-center">
          Aguardando pagamento... Não feche esta janela.
        </p>
        
        <button
          @click="checkPixStatus()"
          :disabled="checkingPix"
          class="w-full py-3 rounded-lg border border-gray-300 font-medium hover:bg-gray-50 disabled:opacity-50"
        >
          {{ checkingPix ? 'Verificando...' : 'Já paguei' }}
        </button>
      </div>
      
      <div v-else class="text-center py-8">
        <div class="animate-spin w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p>Gerando QR Code...</p>
      </div>
    </div>
  </div>
  
  <!-- Modal de bairro sem cobertura de entrega -->
  <div
    v-if="showUncoveredNeighborhoodModal"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click.self="showUncoveredNeighborhoodModal = false"
  >
    <div class="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl text-center">
      <div class="mx-auto w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
        <MapPinIcon class="w-8 h-8 text-red-600" />
      </div>
      <h3 class="text-lg font-bold text-gray-900 mb-2">Bairro não atendido</h3>
      <p class="text-sm text-gray-600 mb-6">
        Esse bairro não é coberto por teleentrega<span v-if="uncoveredNeighborhoodName">: <strong>{{ uncoveredNeighborhoodName }}</strong></span>.
        Verifique o endereço informado ou escolha a opção de retirada no local.
      </p>
      <button
        @click="showUncoveredNeighborhoodModal = false"
        class="w-full px-4 py-2.5 text-white rounded-lg font-medium transition-opacity hover:opacity-90"
        :style="{ backgroundColor: companyStore.primaryColor }"
      >
        Entendi
      </button>
    </div>
  </div>
  
  <!-- Modal de pedido mínimo não atingido -->
  <div
    v-if="showMinOrderModal"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
    @click.self="showMinOrderModal = false"
  >
    <div class="bg-white rounded-2xl max-w-sm w-full p-6 shadow-2xl text-center">
      <div
        class="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4"
        :style="{ backgroundColor: companyStore.primaryColor + '20' }"
      >
        <ShoppingCartIcon class="w-8 h-8" :style="{ color: companyStore.primaryColor }" />
      </div>
      <h3 class="text-lg font-bold text-gray-900 mb-2">Pedido mínimo não atingido</h3>
      <p class="text-sm text-gray-600 mb-6">
        O pedido mínimo para entrega é de <strong>{{ formatCurrency(minOrderValue) }}</strong>.
        Adicione mais itens para concluir sua compra.
      </p>
      <button
        @click="showMinOrderModal = false"
        class="w-full px-4 py-2.5 text-white rounded-lg font-medium transition-opacity hover:opacity-90"
        :style="{ backgroundColor: companyStore.primaryColor }"
      >
        Entendi
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  TruckIcon,
  BuildingStorefrontIcon,
  CreditCardIcon,
  BanknotesIcon,
  QrCodeIcon,
  ArrowLeftIcon,
  PhotoIcon,
  InformationCircleIcon,
  MapPinIcon,
  ShoppingCartIcon
} from '@heroicons/vue/24/outline'
import { useCartStore, useCompanyStore } from '@/stores'
import { deliveryApi, orderApi, paymentApi } from '@/utils/api'
import { formatCurrency } from '@/utils/formatters'
import { notifyError, notifyInfo, notifySuccess } from '@/utils/notifications'
import type { NeighborhoodDelivery } from '@/types'

const router = useRouter()
const cartStore = useCartStore()
const companyStore = useCompanyStore()

// Carrega carrinho imediatamente (antes da renderização)
cartStore.loadFromStorage()

const currentStep = ref(0)
const placingOrder = ref(false)
const neighborhoods = ref<NeighborhoodDelivery[]>([])
const mercadoPagoConfig = ref<any>(null)

// PIX Modal
const showPixModal = ref(false)
const pixData = ref<any>(null)
const checkingPix = ref(false)
const createdOrder = ref<any>(null)

// Modal de bairro sem cobertura de entrega
const showUncoveredNeighborhoodModal = ref(false)
const uncoveredNeighborhoodName = ref('')

// Modal de pedido mínimo não atingido
const showMinOrderModal = ref(false)

const steps = [
  { key: 'customer', label: 'Dados pessoais' },
  { key: 'delivery', label: 'Entrega' },
  { key: 'payment', label: 'Pagamento' },
  { key: 'review', label: 'Revisar' }
]

const customer = ref({
  name: '',
  phone: '',
  email: '',
  cpf: ''
})

const delivery = ref({
  type: 'delivery' as 'delivery' | 'pickup',
  address: '',
  neighborhood: '',
  complement: '',
  zip_code: ''
})

const fetchAddressByZipCode = async (cep: string) => {
  const cleanCep = cep.replace(/\D/g, '')
  if (cleanCep.length !== 8) return

  try {
    const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`)
    const data = await response.json()

    if (data.erro) {
      console.warn('CEP não encontrado:', cep)
      return
    }

    // Preenche o endereço
    if (data.logradouro) {
      delivery.value.address = data.logradouro
    }

    if (data.bairro) {
      if (flatFeeEnabled.value) {
        // Taxa única: bairro é campo livre, preenche direto
        delivery.value.neighborhood = data.bairro
      } else if (neighborhoods.value.length > 0) {
        // Taxas por bairro: seleciona o bairro na combobox (match exato ou parcial)
        const bairroLower = data.bairro.toLowerCase()
        const matched = neighborhoods.value.find(
          n => n.name.toLowerCase() === bairroLower
        ) || neighborhoods.value.find(
          n => n.name.toLowerCase().includes(bairroLower) || bairroLower.includes(n.name.toLowerCase())
        )
        if (matched) {
          delivery.value.neighborhood = matched.name
        } else {
          // Bairro sem taxa de entrega cadastrada
          uncoveredNeighborhoodName.value = data.bairro
          showUncoveredNeighborhoodModal.value = true
        }
      }
    }
  } catch (error) {
    console.error('Erro ao buscar CEP:', error)
  }
}

watch(() => delivery.value.zip_code, (newValue) => {
  const cleanCep = newValue.replace(/\D/g, '')
  if (cleanCep.length === 8) {
    fetchAddressByZipCode(cleanCep)
  }
})

const payment = ref({
  method: 'credit_card' as 'credit_card' | 'debit_card' | 'pix' | 'cash' | 'mercado_pago',
  change_for: undefined as number | undefined
})

// Default payment methods if company not loaded
const defaultPaymentMethods = ['credit_card', 'pix', 'cash']

const mercadoPagoEnabled = computed(() => {
  return mercadoPagoConfig.value?.enabled === true
})

const availablePaymentMethods = computed(() => {
  const configured = companyStore.paymentMethods.length > 0 
    ? companyStore.paymentMethods 
    : defaultPaymentMethods
  // Com Mercado Pago ativo, o cartão é escolhido dentro do checkout online do MP;
  // o PIX aparece como opção separada com QR code gerado na própria loja
  if (mercadoPagoEnabled.value) {
    const methods = ['mercado_pago']
    if (configured.includes('pix')) methods.push('pix')
    if (configured.includes('cash')) methods.push('cash')
    return methods
  }
  return configured
})

// Taxa única de entrega: quando definida na empresa, vale para qualquer bairro
const flatFeeEnabled = computed(() => {
  return companyStore.company?.delivery_flat_fee !== null &&
         companyStore.company?.delivery_flat_fee !== undefined
})

const deliveryFee = computed(() => {
  if (delivery.value.type === 'pickup') return 0
  if (flatFeeEnabled.value) return companyStore.company?.delivery_flat_fee || 0
  const neighborhood = neighborhoods.value.find(n => n.name === delivery.value.neighborhood)
  return neighborhood?.delivery_fee || 0
})

// Pedido mínimo: com taxa única vale o da empresa; por bairro, o do bairro selecionado
const minOrderValue = computed(() => {
  if (flatFeeEnabled.value) return companyStore.company?.min_order_value || 0
  const neighborhood = neighborhoods.value.find(n => n.name === delivery.value.neighborhood)
  return neighborhood?.min_order_value || 0
})

const isBelowMinOrder = computed(() => {
  return minOrderValue.value > 0 && cartStore.subtotal < minOrderValue.value
})

const total = computed(() => {
  return cartStore.subtotal + deliveryFee.value - cartStore.discount
})

const getPaymentIcon = (method: string) => {
  const icons: Record<string, any> = {
    credit_card: CreditCardIcon,
    debit_card: CreditCardIcon,
    pix: QrCodeIcon,
    cash: BanknotesIcon,
    mercado_pago: CreditCardIcon
  }
  return icons[method] || CreditCardIcon
}

const getImageUrl = (url: string) => {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return url
}

const selectPaymentMethod = (method: string) => {
  payment.value.method = method as any
}

const nextStep = () => {
  // Validação básica
  if (currentStep.value === 0) {
    if (!customer.value.name || !customer.value.phone) {
      notifyError('Preencha todos os campos obrigatórios')
      return
    }
  }
  
  if (currentStep.value === 1 && delivery.value.type === 'delivery') {
    if (!delivery.value.address || !delivery.value.neighborhood) {
      notifyError('Preencha o endereço de entrega')
      return
    }
    if (isBelowMinOrder.value) {
      showMinOrderModal.value = true
      return
    }
  }
  
  currentStep.value++
}

const placeOrder = async () => {
  // Bloqueia pedido abaixo do valor mínimo
  if (delivery.value.type === 'delivery' && isBelowMinOrder.value) {
    showMinOrderModal.value = true
    return
  }
  
  placingOrder.value = true
  
  try {
    if (!companyStore.company?.id) {
      notifyError('Empresa não carregada')
      placingOrder.value = false
      return
    }
    const companyId = companyStore.company.id
    
    // Prepara os dados do cliente
    const customerData = {
      name: customer.value.name,
      phone: customer.value.phone,
      email: customer.value.email || undefined,
      cpf: customer.value.cpf || undefined,
      address: delivery.value.type === 'delivery' ? delivery.value.address : undefined,
      neighborhood: delivery.value.type === 'delivery' ? delivery.value.neighborhood : undefined,
      complement: delivery.value.type === 'delivery' ? delivery.value.complement : undefined,
      zip_code: delivery.value.type === 'delivery' ? delivery.value.zip_code : undefined
    }
    
    const orderData = {
      company_id: companyId,
      customer: customerData,
      items: cartStore.getItemsForOrder(),
      order_type: delivery.value.type,
      delivery_address: delivery.value.type === 'delivery' ? delivery.value.address : undefined,
      delivery_neighborhood: delivery.value.type === 'delivery' ? delivery.value.neighborhood : undefined,
      delivery_complement: delivery.value.type === 'delivery' ? delivery.value.complement : undefined,
      delivery_zip_code: delivery.value.type === 'delivery' ? delivery.value.zip_code : undefined,
      payment_method: payment.value.method,
      change_for: payment.value.method === 'cash' ? payment.value.change_for : undefined,
      customer_notes: undefined
    }
    
    console.log('Enviando pedido:', orderData)
    
    const response = await orderApi.create(orderData)
    createdOrder.value = response.data
    
    // Salva número do pedido no localStorage para acompanhamento
    if (response.data.order_number) {
      localStorage.setItem('lastOrderNumber', response.data.order_number)
      if (companyStore.company?.id) {
        localStorage.setItem('lastOrderCompanyId', String(companyStore.company.id))
      }
    }
    
    // Processa o pagamento conforme o método
    if (payment.value.method === 'mercado_pago' && mercadoPagoEnabled.value) {
      // Checkout online do MP: cartão/PIX/etc. são escolhidos lá dentro
      await processCheckoutPro(response.data.id)
    } else if (payment.value.method === 'pix' && mercadoPagoEnabled.value) {
      await processPixPayment(response.data.id)
    } else if ((payment.value.method === 'credit_card' || payment.value.method === 'debit_card') && mercadoPagoEnabled.value) {
      await processCheckoutPro(response.data.id)
    } else {
      // Pagamento em dinheiro ou sem MP configurado
      cartStore.clearCart()
      router.push({
        name: 'order-confirmation',
        params: { orderNumber: response.data.order_number }
      })
    }
  } catch (error: any) {
    console.error('Erro ao criar pedido:', error)
    notifyError(error.response?.data?.detail || 'Erro ao criar pedido. Tente novamente.')
  } finally {
    placingOrder.value = false
  }
}

const processPixPayment = async (orderId: number) => {
  try {
    const response = await paymentApi.createPix(orderId)
    pixData.value = response.data
    showPixModal.value = true
  } catch (error: any) {
    console.error('Erro ao gerar PIX:', error)
    notifyError('Erro ao gerar QR Code PIX. Tente novamente.')
  }
}

const copyPixCode = () => {
  if (pixData.value?.pix_code) {
    navigator.clipboard.writeText(pixData.value.pix_code)
    notifySuccess('Código PIX copiado!')
  }
}

const checkPixStatus = async (silent = false) => {
  if (!createdOrder.value) return
  
  checkingPix.value = true
  try {
    const response = await paymentApi.getStatus(createdOrder.value.id)
    if (response.data.payment_status === 'paid') {
      stopPixPolling()
      showPixModal.value = false
      cartStore.clearCart()
      if (createdOrder.value?.order_number) {
        localStorage.setItem('lastOrderNumber', createdOrder.value.order_number)
      }
      router.push({
        name: 'order-confirmation',
        params: { orderNumber: createdOrder.value.order_number }
      })
    } else if (!silent) {
      notifyInfo('Pagamento ainda não confirmado. Tente novamente em alguns segundos.')
    }
  } catch (error) {
    if (!silent) {
      console.error('Erro ao verificar status:', error)
      notifyError('Erro ao verificar status do pagamento.')
    }
  } finally {
    checkingPix.value = false
  }
}

// Verifica o pagamento automaticamente enquanto a modal PIX estiver aberta
let pixPollInterval: ReturnType<typeof setInterval> | null = null

const stopPixPolling = () => {
  if (pixPollInterval) {
    clearInterval(pixPollInterval)
    pixPollInterval = null
  }
}

watch(showPixModal, (open) => {
  stopPixPolling()
  if (open) {
    pixPollInterval = setInterval(() => checkPixStatus(true), 5000)
  }
})

onUnmounted(() => {
  stopPixPolling()
})

const processCheckoutPro = async (orderId: number) => {
  try {
    const response = await paymentApi.createCheckoutPro(orderId)
    if (response.data.checkout_url) {
      // Redireciona para o checkout do Mercado Pago
      window.location.href = response.data.checkout_url
    } else {
      notifyError('Erro ao criar checkout. Tente novamente.')
    }
  } catch (error: any) {
    console.error('Erro ao criar checkout:', error)
    notifyError(error.response?.data?.detail || 'Erro ao criar checkout. Tente novamente.')
  }
}

const loadNeighborhoods = async () => {
  if (!companyStore.company?.id) return
  const companyId = companyStore.company.id
  try {
    const res = await deliveryApi.getNeighborhoods(companyId)
    neighborhoods.value = res.data
  } catch (error) {
    console.error('Erro ao carregar bairros:', error)
  }
}

const loadPaymentConfig = async () => {
  if (!companyStore.company?.id) return
  const companyId = companyStore.company.id
  try {
    const res = await paymentApi.getConfig(companyId)
    mercadoPagoConfig.value = res.data.mercado_pago
    // Com MP ativo, a forma padrão passa a ser o checkout online do MP
    if (mercadoPagoConfig.value?.enabled) {
      payment.value.method = 'mercado_pago'
    }
  } catch (error) {
    console.error('Erro ao carregar configuração de pagamento:', error)
  }
}

watch(() => companyStore.company?.id, (newId) => {
  if (newId) {
    loadNeighborhoods()
    loadPaymentConfig()
  }
}, { immediate: true })

onMounted(() => {
  cartStore.loadFromStorage()
})
</script>
